// © Microsoft Corporation. All rights reserved.

import { BaseExperienceConfig, appEnvironment, resolver } from "@msnews/core";
import { FASTElement, attr } from "@microsoft/fast-element";
import { MilestoneDataActions, MilestoneDataConnector, UpdateTimingSignature, getMilestoneDataConnector } from "@msnews/milestone-data";
import { ObjectTelemetryContract, TelemetryObjectBase, TelemetryType } from "@msnews/telemetry-contracts";
import { PageInvalidationReasons, registerStaleRevealWC, trackPageInvalidation } from "@msnews/page-lifecycle-helpers";
import { PerfTimeline, WceInitializationException, incrementCounter, logException, markTtvr, rafTimer, trackSubtreeMutations, tryCreateWCInstancePerfTimeline } from "@msnews/diagnostics";

import { ActionMap } from "@msnews/experiences-redux";
import { ConfigReference } from "@msnews/config-data-types";
import { ExperienceIds } from "@msnews/experiences-constants";
import { TelemetryObject } from "@msnews/telemetry-core";
import { getPropObjectFromToken } from "@msnews/experiences-web-component-loader";
import { runShadowDomUpdate } from "./WebComponentUtilities";

const PROPS_TOKEN = "props-token";

/**
 * Base class for Web Component Experiences.
 */
export abstract class WebComponentExperience<TConfig extends BaseExperienceConfig<TStrings>, TStrings> extends FASTElement {

    /**
     * End marker callback to report when the experience is visually ready.
     * Note that the markTimeToVisuallyReady method must be explicitly called by the experience
     * or this will never be logged.
     */
    private initToVisuallyReadyEndMeasure?: () => void;

    /**
     * The unique perf timeline object for this experience instance.
     * Experience owners should use this to demark any custom operations that you want
     * to appear on the app trace timeline.
     */
    protected perfTimeline?: PerfTimeline;

    /**
     * Invoked when the web component is connected to the DOM.
     */
    connectedCallback() {
        // Kick off async initialization of the web component.
        this.initializeAsync();
    }

    /**
     * Initializes the web component experience.
     */
    private async initializeAsync(): Promise<void> {

        const experienceType: string = this.getExperienceType();

        const perfTimeline = this.perfTimeline = tryCreateWCInstancePerfTimeline(experienceType, this.instanceId);
        const initEndMeasure = perfTimeline && perfTimeline.startMeasure("init").endMeasure;
        incrementCounter("WCE.Count");

        this.reportStartTimeToMilestoneConnector(MilestoneDataActions.updateVisuallyReadyTiming);

        try {
            let configId: ConfigReference | string;
            if (appEnvironment.IsGitConfigs) {
                // With Git configs, build the config ref object
                configId = { instanceSrc: this.instanceSrc, experienceType, sharedNs: this.sharedNs };
            } else {
                // With CMS configs, treat the instanceSrc as a config index href
                configId = this.instanceSrc;
            }

            // We will sometimes set config directly when testing
            if (!this.config) {
                // TODO: Update to use synchronous fetch after Git config migration, as this should already be warmed up at this point.
                const configWrapper = await resolver.getConfig(configId);
                this.config = configWrapper.properties;
            }

            this.strings = this.config.localizedStrings;

            // If we don't have a telemetry object yet, populate it
            if (!this.telemetryObject) {
                const telemetryContract: ObjectTelemetryContract = this.getTelemetryContract();
                this.telemetryObject = new TelemetryObject(telemetryContract);
            }

            // If experience is set up for it, register for stale reveal callbacks.
            const { stalenessInfo } = this.config;
            if (stalenessInfo && stalenessInfo.isStaleCheckRequired) {
                registerStaleRevealWC({
                    experienceType,
                    thresholdMinutes: stalenessInfo.staleThreshold,
                    asyncCallback: this.revalidateExperienceStaleState.bind(this)
                });
            }

            // Let the implementation run code after config is available and before templates are initialized.
            const promise = this.experienceConnected();
            if (promise) {
                await promise;
            }

            // Tell the base FASTElement to start up the Shadow DOM and populate with the template
            super.connectedCallback();

            // Let the implementation run code after the shadow DOM has been populated with the template
            this.shadowDomPopulated();

            if (this.perfTimeline) {
                trackSubtreeMutations(this, this.perfTimeline, runShadowDomUpdate);
            }

        } catch (initException) {
            // Catch exception thrown in experienceConnected, or shadowDomPopulated or if any exception is thrown in this code block, but
            // not when observables trigger template updates. See bug 3539414.
            logException(initException, WceInitializationException, "Exception web component experience initializeAsync.");
            initEndMeasure && initEndMeasure("error");
        } finally {
            initEndMeasure && initEndMeasure();
            this.initToVisuallyReadyEndMeasure = perfTimeline && perfTimeline.startMeasure("ready").endMeasure;
        }
    }

    /**
     * Run after config initializtion and before Shadow DOM creation. Any experience property initializion should be done here.
     */
    experienceConnected(): Promise<void> | void {
    }

    /**
     * Run after the Shadow DOM has been created and populated with the template.
     */
    shadowDomPopulated() {
    }

    /**
     * Called when the experience is configured to check stale state. Override this to implement logic to refresh data when the experience is stale.
     * The promise should resolve when the refresh is complete.
     */
    revalidateExperienceStaleState(): Promise<void> {
        return Promise.resolve(undefined);
    }

    /**
     * Returns the experience type.
     */
    abstract getExperienceType(): ExperienceIds;

    /**
     * Track experience changes for pageReveal
     * @param invalidationReason - Page invalidation reason
     */
    protected trackPageInvalidationOnReveal(invalidationReason: PageInvalidationReasons): void {
        // Track changes in persistent store for page invalidation on pageReveal
        trackPageInvalidation(invalidationReason);
    }

    /**
     * Gets the telemetry contract for the experience.
     * @returns The telemetry contract to use.
     */
    protected getTelemetryContract(): ObjectTelemetryContract {
        return {
            type: TelemetryType.Module,
            name: this.getExperienceType()
        };
    }

    /**
     * Marks TTVR for this experience.
     * @param useRafTimer - Optional flag to indicate that this call should use a RAF+setTimeout
     *  combo to report the TTVR metric. Default is false.
     */
    protected markTimeToVisuallyReady(useRafTimer = false) {

        if (useRafTimer) {
            rafTimer(() => this.markTimeToVisuallyReady(false));
            return;
        }

        markTtvr(this.getExperienceType());
        this.reportEndTimeToMilestoneConnector(MilestoneDataActions.updateVisuallyReadyTiming);
        this.initToVisuallyReadyEndMeasure && this.initToVisuallyReadyEndMeasure();
    }

    /**
     * Reports start time to the milestone connector, using the given action.
     * @param actionMap The action to use to report the start time to.
     */
    private reportStartTimeToMilestoneConnector(actionMap: ActionMap<UpdateTimingSignature>) {
        this.reportTimeToMilestoneConnector(actionMap, performance.now());
    }

    /**
     * Reports end time to the milestone connector, using the given action.
     * @param actionMap The action to use to report the end time to.
     */
    private reportEndTimeToMilestoneConnector(actionMap: ActionMap<UpdateTimingSignature>) {
        this.reportTimeToMilestoneConnector(actionMap, undefined, performance.now());
    }

    /**
     * Common helper to send a milestone timing update for a specific action.
     */
    private reportTimeToMilestoneConnector(actionMap: ActionMap<UpdateTimingSignature>, startTime?: number, endTime?: number): void {
        const milestoneDataConnector: MilestoneDataConnector = getMilestoneDataConnector();
        if (milestoneDataConnector) {
            actionMap.getActionSender(milestoneDataConnector)
                .send({
                    experienceType: this.getExperienceType(),
                    experienceInstance: this.instanceId,
                    startTime,
                    endTime
                });
        }
    }

    /**
     * This is the instanceSrc from the ConfigReference, OR the config href if using CMS config. The CMS config capability will be removed
     * after the Git config transition.
     */
    @attr({ attribute: "config-instance-src" })
    instanceSrc: string;

    /**
     * The shared namespace for the config.
     */
    @attr({ attribute: "config-shared-ns" })
    sharedNs: string;

    /**
     * The instance ID for this experience.
     */
    @attr({ attribute: "instance-id" })
    instanceId: string;

    /** Config for this experience. */
    config: TConfig;

    /** Localized strings for this experience. */
    strings: TStrings;

    /** The telemetry object for this experience. */
    telemetryObject: TelemetryObjectBase;

    /** Token for objects that are passed from parent React component to WC child. */
    @attr({ attribute: PROPS_TOKEN })
    propsToken: string;

    attributeChangedCallback(attrName: string, oldVal: any, newVal: any) {
        super.attributeChangedCallback(attrName, oldVal, newVal);

        if (attrName === PROPS_TOKEN) {
            const props = getPropObjectFromToken(newVal);
            Object.assign(this, props);
        }
    }
}